/**
 * The programmes whose teams this account may manage, as the way into each.
 *
 * The master list of the teams screen: every programme visible, so the reader
 * sees what is available before choosing.
 *
 * @module components/teams/ProgrammePicker
 */

import React from "react";

import { WorkflowStateBadge } from "@/components/shared/WorkflowStateBadge";
import { EmptyState, MasterItem, MasterList } from "@/components/ui";
import type { ProgrammeSummary } from "@/platform";

export interface ProgrammePickerProps {
  /** Already narrowed by the filter that sits above the list. */
  programmes: readonly ProgrammeSummary[];
  /** The programme on screen, so the list can mark it. */
  selectedId?: string;
  onSelect: (programmeId: string) => void;
}

/**
 * @example
 * ```tsx
 * <ProgrammePicker programmes={rows} selectedId={id} onSelect={open} />
 * ```
 */
export function ProgrammePicker({
  programmes,
  selectedId = undefined,
  onSelect,
}: ProgrammePickerProps): React.ReactElement {
  if (programmes.length === 0) {
    return (
      <div className="p-3">
        <EmptyState
          icon="funnel"
          title="No programmes match these filters"
          description="Clear a filter to see the rest of the programmes you manage."
          density="row"
          data-testid="programme-teams-empty"
        />
      </div>
    );
  }
  // The same badge on every row says nothing, so it shows only where the
  // states differ and the reader has something to tell them apart by.
  const statesDiffer = new Set(programmes.map((programme) => programme.workflowState)).size > 1;
  return (
    <MasterList data-testid="programme-teams-list">
      {programmes.map((programme) => (
        <MasterItem
          key={programme.id}
          title={programme.title}
          // The award, not the id: most programmes carry no code, and an
          // internal identifier under a title is noise to the reader.
          meta={programme.programmeCode ?? programme.awardType}
          selected={programme.id === selectedId}
          onSelect={() => {
            onSelect(programme.id);
          }}
          data-testid={`programme-teams-option-${programme.id}`}
        >
          {statesDiffer ? <WorkflowStateBadge state={programme.workflowState} size="sm" /> : null}
        </MasterItem>
      ))}
    </MasterList>
  );
}
